import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { devWarn } from "@/lib/uiStorage";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password, rememberMe = false) => {
    const response = await axios.post(`${API}/auth/login/json`, { email, password, remember_me: rememberMe });
    setUser(response.data.user);
    return response.data;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    setUser({
      user_id: response.data.user_id,
      email: response.data.email,
      name: response.data.name,
      package_id: response.data.package_id,
      email_verified: response.data.email_verified,
    });
    return response.data;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Preserve the ?plan= query param across the OAuth round-trip so the
    // callback page can auto-redirect to Stripe checkout for paid signups.
    const params = new URLSearchParams(window.location.search);
    const plan = params.get("plan");
    if (plan) {
      try {
        sessionStorage.setItem("lumora.postAuthPlan", plan);
      } catch (e) {
        // Safari Private Mode disables sessionStorage — non-fatal, paid
        // intent just won't survive the OAuth round-trip (user lands on /studio).
        if (process.env.NODE_ENV !== "production") {
          devWarn("sessionStorage unavailable; paid plan intent will be dropped", e?.message);
        }
      }
    }
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      toast.error("Logout failed, please try again");
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, loginWithGoogle, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};
